import React, { useState } from "react";
import moment from "moment";
import { toast } from "react-toastify";
import Header from "../../components/content_wrapper/header";
import Pagination from "../../utils/pagination";
import { useGetCapacityAlerts } from "../../hooks/capacityAlerts/useGetCapacityAlerts";
import {
  useGetCapacityAlertSettings,
} from "../../hooks/capacityAlerts/useGetCapacityAlertSettings";
import { useUpdateCapacityAlertSettings } from "../../hooks/capacityAlerts/useUpdateCapacityAlertSettings";
import { useGetLapsedSubscriptionAlerts } from "../../hooks/lapsedSubscriptions/useGetLapsedSubscriptionAlerts";
import {
  useKeyDisposition,
  type KeyDispositionAction,
} from "../../hooks/lapsedSubscriptions/useKeyDisposition";
import { useGetKeyHost } from "../../hooks/keyHostManagementApi/getKeyHost";
import { useGetKeyHostAuthorizationChanges } from "../../hooks/keyManagement/useGetKeyHostAuthorizationChanges";

type TabId = "alerts" | "hostChanges" | "settings" | "lapsed";

const DISPOSITION_LABELS: Record<string, string> = {
  pending: "Pending",
  returned_by_post: "Posted back to owner",
  returned_to_owner: "Returned to owner",
  destroyed: "Destroyed",
  retired: "Retired",
  reactivated: "Reactivated",
};

const fmtDate = (d: any) => {
  if (!d) return "—";
  const m = typeof d === "number" ? moment.unix(d) : moment(d);
  return m.isValid() ? m.format("MMM DD, YYYY h:mm A") : "—";
};

const CapacityAlertsScreen: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabId>("alerts");

  // ── Alerts tab ──
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsLimit, setAlertsLimit] = useState(20);
  const [alertsStatus, setAlertsStatus] = useState<"active" | "resolved" | "all">("active");
  const { data: alertsResp, isLoading: alertsLoading } = useGetCapacityAlerts(
    { status: alertsStatus, page: alertsPage, limit: alertsLimit },
    activeTab === "alerts"
  );

  // ── Host Changes tab ──
  const [hostChangesPage, setHostChangesPage] = useState(1);
  const [hostChangesLimit, setHostChangesLimit] = useState(20);
  const { data: hostChangesResp, isLoading: hostChangesLoading } = useGetKeyHostAuthorizationChanges(
    { page: hostChangesPage, limit: hostChangesLimit },
    activeTab === "hostChanges"
  );

  // ── Settings tab ──
  const { data: settingsResp, isLoading: settingsLoading } = useGetCapacityAlertSettings(
    activeTab === "settings"
  );
  const { mutate: updateSettings, isLoading: savingSettings } = useUpdateCapacityAlertSettings();
  const [globalThreshold, setGlobalThreshold] = useState<number | null>(null);

  const effectiveGlobalThreshold =
    globalThreshold ?? settingsResp?.data?.global?.lowSlotThreshold ?? 1;

  const handleSaveGlobalThreshold = () => {
    updateSettings(
      { scope: "global", low_slot_threshold: Number(effectiveGlobalThreshold) },
      {
        onSuccess: () => { toast.success("Global capacity threshold updated"); },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Failed to update threshold");
        },
      }
    );
  };

  // Per-host override creation
  const [overrideHostSearch, setOverrideHostSearch] = useState("");
  const [overrideHostId, setOverrideHostId] = useState("");
  const [overrideThreshold, setOverrideThreshold] = useState(1);
  const { data: overrideHostOptions } = useGetKeyHost({
    page: "1",
    limit: "50",
    search: overrideHostSearch,
    enabled: activeTab === "settings",
  });

  const handleAddOverride = () => {
    if (!overrideHostId) { toast.error("Please select a key host"); return; }
    updateSettings(
      { scope: "host", key_host_id: overrideHostId, low_slot_threshold: Number(overrideThreshold) },
      {
        onSuccess: () => {
          toast.success("Per-host override saved");
          setOverrideHostId("");
          setOverrideThreshold(1);
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Failed to save override");
        },
      }
    );
  };

  // ── Lapsed subscriptions tab ──
  const [lapsedPage, setLapsedPage] = useState(1);
  const [lapsedLimit, setLapsedLimit] = useState(20);
  const [lapsedStatus, setLapsedStatus] = useState<"active" | "resolved" | "all">("active");
  const { data: lapsedResp, isLoading: lapsedLoading } = useGetLapsedSubscriptionAlerts(
    { status: lapsedStatus, page: lapsedPage, limit: lapsedLimit },
    activeTab === "lapsed"
  );
  const { mutate: dispositionKey, isLoading: dispositioning } = useKeyDisposition();
  const [pendingKeyId, setPendingKeyId] = useState<string | null>(null);

  const handleDisposition = (keyId: string, action: KeyDispositionAction) => {
    const confirmText = {
      returned_by_post: "Confirm the key is being posted back to the owner, and retire it (releases its slot)?",
      destroyed: "Confirm the key has been securely destroyed, and retire it (releases its slot)?",
      returned_to_owner: "Confirm the key has been handed back to the owner, and retire it (releases its slot)?",
    }[action];
    if (!window.confirm(confirmText)) return;
    setPendingKeyId(keyId);
    dispositionKey(
      { key_id: keyId, action },
      {
        onSuccess: () => { toast.success("Key dispositioned and retired"); },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || "Failed to disposition the key");
        },
        onSettled: () => setPendingKeyId(null),
      }
    );
  };

  return (
    <div className="content-wrapper">
      <Header title="Capacity Alerts" />
      <div className="main-wrapper px-4 pt-4 pb-4">
        <div className="wrapper-card mb-4">
          <ul className="nav nav-tabs">
            <li className="nav-item">
              <button
                className={`nav-link${activeTab === "alerts" ? " active" : ""}`}
                onClick={() => setActiveTab("alerts")}
              >
                Low-Capacity Alerts
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link${activeTab === "hostChanges" ? " active" : ""}`}
                onClick={() => setActiveTab("hostChanges")}
              >
                Host Changes
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link${activeTab === "lapsed" ? " active" : ""}`}
                onClick={() => setActiveTab("lapsed")}
              >
                Lapsed Subscriptions
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link${activeTab === "settings" ? " active" : ""}`}
                onClick={() => setActiveTab("settings")}
              >
                Settings
              </button>
            </li>
          </ul>

          <div className="tab-content pt-4">
            {activeTab === "alerts" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted small mb-0">
                    A host is flagged here once its free-slot count drops to or below its
                    threshold.
                  </p>
                  <select
                    className="form-select w-auto"
                    value={alertsStatus}
                    onChange={(e) => {
                      setAlertsStatus(e.target.value as any);
                      setAlertsPage(1);
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="resolved">Resolved</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="table-blk">
                  <div className="table-responsive">
                    <table className="display border-0 w-100">
                      <thead>
                        <tr>
                          <th>Store</th>
                          <th>Free Slots</th>
                          <th>Threshold</th>
                          <th>Status</th>
                          <th>Triggered</th>
                          <th>Resolved</th>
                        </tr>
                      </thead>
                      <tbody>
                        {alertsResp?.data?.map((alert: any) => (
                          <tr key={alert._id}>
                            <td>{alert.keyHostId?.storeName || alert.key_host_id?.store_name || "—"}</td>
                            <td>{alert.freeSlotsAtTrigger ?? alert.free_slots_at_trigger}</td>
                            <td>{alert.thresholdAtTrigger ?? alert.threshold_at_trigger}</td>
                            <td>
                              <span className={`badge ${alert.status === "active" ? "bg-danger" : "bg-success"}`}>
                                {alert.status}
                              </span>
                            </td>
                            <td className="small">{fmtDate(alert.triggeredAt ?? alert.triggered_at)}</td>
                            <td className="small">{fmtDate(alert.resolvedAt ?? alert.resolved_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!alertsLoading && !alertsResp?.data?.length && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                      <i className="fa-solid fa-check-circle fa-2x mb-2" />
                      <h5 className="mb-1">No Alerts</h5>
                    </div>
                  )}
                </div>
                <Pagination
                  currentPage={alertsPage}
                  totalPages={alertsResp?.pagination?.pages || 1}
                  handlePrevPage={() => setAlertsPage((p) => Math.max(1, p - 1))}
                  handleNextPage={() => setAlertsPage((p) => p + 1)}
                  handleRowLimitChange={(limit) => {
                    setAlertsLimit(limit);
                    setAlertsPage(1);
                  }}
                />
              </div>
            )}

            {activeTab === "hostChanges" && (
              <div>
                <p className="text-muted small mb-3">
                  Every owner- or admin-initiated change to a key's authorized host, across
                  all keys and stores.
                </p>
                <div className="table-blk">
                  <div className="table-responsive">
                    <table className="display border-0 w-100">
                      <thead>
                        <tr>
                          <th>Key</th>
                          <th>From</th>
                          <th>To</th>
                          <th>Changed By</th>
                          <th>Reason</th>
                          <th>Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {hostChangesResp?.data?.map((change: any) => (
                          <tr key={change._id}>
                            <td>{change.keyId?.keyName || change.key_id?.key_name || "—"}</td>
                            <td>{change.fromKeyHostStoreId?.storeName || change.from_key_host_store_id?.store_name || "—"}</td>
                            <td>{change.toKeyHostStoreId?.storeName || change.to_key_host_store_id?.store_name || "—"}</td>
                            <td>
                              <span className="badge bg-secondary text-uppercase">
                                {change.changedByType || change.changed_by_type}
                              </span>
                            </td>
                            <td className="small">{change.reason || "—"}</td>
                            <td className="small">{fmtDate(change.createdAt ?? change.created_at)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {!hostChangesLoading && !hostChangesResp?.data?.length && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                      <i className="fa-solid fa-shuffle fa-2x mb-2" />
                      <h5 className="mb-1">No Host Changes Yet</h5>
                    </div>
                  )}
                </div>
                <Pagination
                  currentPage={hostChangesPage}
                  totalPages={hostChangesResp?.pagination?.pages || 1}
                  handlePrevPage={() => setHostChangesPage((p) => Math.max(1, p - 1))}
                  handleNextPage={() => setHostChangesPage((p) => p + 1)}
                  handleRowLimitChange={(limit) => {
                    setHostChangesLimit(limit);
                    setHostChangesPage(1);
                  }}
                />
              </div>
            )}

            {activeTab === "lapsed" && (
              <div>
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <p className="text-muted small mb-0">
                    Subscription lapsed while a physical key is still at a host. Coordinate
                    retrieval, then record how the key was handled — that retires the key
                    and releases its still-held slot.
                  </p>
                  <select
                    className="form-select w-auto"
                    value={lapsedStatus}
                    onChange={(e) => {
                      setLapsedStatus(e.target.value as any);
                      setLapsedPage(1);
                    }}
                  >
                    <option value="active">Pending Disposition</option>
                    <option value="resolved">Resolved</option>
                    <option value="all">All</option>
                  </select>
                </div>
                <div className="table-blk">
                  <div className="table-responsive">
                    <table className="display border-0 w-100">
                      <thead>
                        <tr>
                          <th>Key</th>
                          <th>Owner</th>
                          <th>Store</th>
                          <th>Lapsed At</th>
                          <th>Status</th>
                          <th>Disposition</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {lapsedResp?.data?.map((alert: any) => {
                          const keyId =
                            alert.keyId?._id || alert.key_id?._id || alert.keyId || alert.key_id;
                          const busy = dispositioning && pendingKeyId === keyId;
                          return (
                          <tr key={alert._id}>
                            <td>{alert.keyId?.keyName || alert.key_id?.key_name || "—"}</td>
                            <td>{alert.ownerId?.name || alert.owner_id?.name || "—"}</td>
                            <td>{alert.keyHostStoreId?.storeName || alert.key_host_store_id?.store_name || "—"}</td>
                            <td className="small">{fmtDate(alert.lapsedAt ?? alert.lapsed_at)}</td>
                            <td>
                              <span className={`badge ${alert.status === "active" ? "bg-warning text-dark" : "bg-success"}`}>
                                {alert.status === "active" ? "Pending Disposition" : "Resolved"}
                              </span>
                              {alert.note ? (
                                <div className="small text-muted mt-1" style={{ maxWidth: 260 }}>{alert.note}</div>
                              ) : null}
                            </td>
                            <td className="small">
                              {alert.status === "active"
                                ? "—"
                                : DISPOSITION_LABELS[alert.disposition] || alert.disposition || "—"}
                            </td>
                            <td>
                              {alert.status === "active" && (
                                <div className="btn-group btn-group-sm" role="group">
                                  <button
                                    className="btn btn-outline-primary"
                                    disabled={busy}
                                    title="Key is being posted back to the owner"
                                    onClick={() => handleDisposition(keyId, "returned_by_post")}
                                  >
                                    Posted back
                                  </button>
                                  <button
                                    className="btn btn-outline-primary"
                                    disabled={busy}
                                    title="Key handed back to the owner in person"
                                    onClick={() => handleDisposition(keyId, "returned_to_owner")}
                                  >
                                    To owner
                                  </button>
                                  <button
                                    className="btn btn-outline-danger"
                                    disabled={busy}
                                    title="Key securely destroyed per the Key Agreement"
                                    onClick={() => handleDisposition(keyId, "destroyed")}
                                  >
                                    Destroyed
                                  </button>
                                </div>
                              )}
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {!lapsedLoading && !lapsedResp?.data?.length && (
                    <div className="d-flex flex-column align-items-center justify-content-center py-5 text-muted">
                      <i className="fa-solid fa-check-circle fa-2x mb-2" />
                      <h5 className="mb-1">Nothing Pending Disposition</h5>
                    </div>
                  )}
                </div>
                <Pagination
                  currentPage={lapsedPage}
                  totalPages={lapsedResp?.pagination?.pages || 1}
                  handlePrevPage={() => setLapsedPage((p) => Math.max(1, p - 1))}
                  handleNextPage={() => setLapsedPage((p) => p + 1)}
                  handleRowLimitChange={(limit) => {
                    setLapsedLimit(limit);
                    setLapsedPage(1);
                  }}
                />
              </div>
            )}

            {activeTab === "settings" && !settingsLoading && (
              <div style={{ maxWidth: 480 }}>
                <h5 className="mb-3">Global Default Threshold</h5>
                <p className="text-muted small">
                  A host is flagged as low-capacity once its free slots drop to or below this
                  number. Applies to every host unless overridden below.
                </p>
                <div className="mb-3 d-flex align-items-center gap-2">
                  <input
                    type="number"
                    min={0}
                    className="form-control"
                    style={{ maxWidth: 120 }}
                    value={effectiveGlobalThreshold}
                    onChange={(e) => setGlobalThreshold(Number(e.target.value))}
                  />
                  <button
                    className="btn btn-primary"
                    disabled={savingSettings}
                    onClick={handleSaveGlobalThreshold}
                  >
                    Save
                  </button>
                </div>

                <h5 className="mt-4 mb-3">Per-Host Overrides</h5>
                {settingsResp?.data?.hostOverrides?.length ? (
                  <table className="display border-0 w-100 mb-3">
                    <thead>
                      <tr>
                        <th>Store</th>
                        <th>Threshold</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settingsResp.data.hostOverrides.map((override: any) => (
                        <tr key={override._id}>
                          <td>{override.keyHostId?.storeName || override.key_host_id?.store_name || "—"}</td>
                          <td>{override.lowSlotThreshold ?? override.low_slot_threshold}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-muted small">No per-host overrides yet.</p>
                )}

                <div className="mb-2">
                  <label className="form-label fw-semibold">Add Override</label>
                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Search by store name…"
                    value={overrideHostSearch}
                    onChange={(e) => setOverrideHostSearch(e.target.value)}
                  />
                  <div className="d-flex align-items-center gap-2">
                    <select
                      className="form-select"
                      value={overrideHostId}
                      onChange={(e) => setOverrideHostId(e.target.value)}
                    >
                      <option value="">Select a key host…</option>
                      {overrideHostOptions?.data?.keyHosts?.map((host: any) => (
                        <option key={host._id} value={host._id}>
                          {host.storeName || host.store_name || host._id}
                        </option>
                      ))}
                    </select>
                    <input
                      type="number"
                      min={0}
                      className="form-control"
                      style={{ maxWidth: 100 }}
                      value={overrideThreshold}
                      onChange={(e) => setOverrideThreshold(Number(e.target.value))}
                    />
                    <button
                      className="btn btn-primary"
                      disabled={savingSettings || !overrideHostId}
                      onClick={handleAddOverride}
                    >
                      Save
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CapacityAlertsScreen;
